import { runInAction } from 'mobx'
import { Observable } from 'rxjs'

/**
 * Capable of being activated.
 */
export interface Activatable {
  /**
   * Called when the implementation is actively used.
   */
  activate(): unknown
}

/**
 * Capable of being deactivated.
 */
export interface Deactivatable {
  /**
   * Called when the implementation is no longer actively used.
   */
  deactivate(): unknown
}

/**
 * Indicates that the implementation can be activated and deactivated.
 */
export interface ActivationLifecycle extends Activatable, Deactivatable {}

/**
 * Activates the view model with its parent only when the predicate holds at
 * that time, and always deactivates it with the parent.
 *
 * @param predicate
 * @param vm
 */
export function activeWhen(
  predicate: () => boolean,
  vm: Activatable & Deactivatable,
): Activatable & Deactivatable {
  return {
    activate: () => predicate() && vm.activate(),
    deactivate: () => vm.deactivate(),
  }
}

/**
 * Activates the view model when subscribed and deactivates it on unsubscribe,
 * so a lifecycle-managed stream can bring a view model in on demand and have
 * it deactivated together with the lifecycle.
 *
 * @param vm
 */
export function activation$(
  vm: Activatable & Deactivatable,
): Observable<never> {
  return new Observable<never>(() => {
    runInAction(() => vm.activate())
    return () => vm.deactivate()
  })
}
